import { reactionArrowPrimitives } from '@peruselab/structure-editor/rendering/reactionArrowGeometry';
import type {
  ReactionArrowAnnotationKind,
  ReactionArrowOverlay
} from '@peruselab/structure-editor/chemistry/reactionParticipant';

interface ReactionArrowGlyphProps {
  readonly kind: ReactionArrowAnnotationKind;
  readonly overlay?: ReactionArrowOverlay;
}

// #395 — the left-rail / composer glyph for a reaction (scheme) arrow. Drawn from the SAME
// geometry the canvas uses (rendering/reactionArrowGeometry.ts), so the rail icon is exactly the
// mark that gets placed. `currentColor` throughout, so it inverts for dark / coloured themes; the
// host control carries the accessible name.
export function ReactionArrowGlyph({ kind, overlay }: ReactionArrowGlyphProps) {
  const vertical = kind === 'vertical-up' || kind === 'vertical-equilibrium';
  const viewW = vertical ? 28 : 60;
  const viewH = 30;
  const inset = 4;
  const from = vertical ? { x: viewW / 2, y: viewH - inset } : { x: inset, y: viewH / 2 };
  const to = vertical ? { x: viewW / 2, y: inset } : { x: viewW - inset, y: viewH / 2 };
  const primitives = reactionArrowPrimitives(from, to, kind, overlay, 2);

  return (
    <svg
      className="reaction-arrow-glyph"
      viewBox={`0 0 ${viewW} ${viewH}`}
      aria-hidden="true"
      focusable="false"
      data-kind={kind}
      data-overlay={overlay}
    >
      <ReactionArrowShapes primitives={primitives} />
    </svg>
  );
}

/** #749 -- the arrow's shapes (from reactionArrowPrimitives), for a glyph or a larger drawing. */
export function ReactionArrowShapes({
  primitives
}: {
  readonly primitives: ReturnType<typeof reactionArrowPrimitives>;
}) {
  return (
    <>
      {primitives.map((primitive, index) => {
        if (primitive.t === 'polygon') {
          return (
            <polygon
              key={index}
              points={primitive.points.map((point) => `${point.x},${point.y}`).join(' ')}
              fill={primitive.filled ? 'currentColor' : 'none'}
              stroke={primitive.head ? 'none' : 'currentColor'}
            />
          );
        }
        if (primitive.t === 'path') {
          return (
            <path
              key={index}
              d={primitive.d}
              fill="none"
              stroke="currentColor"
              strokeLinecap="butt"
              strokeDasharray={primitive.dashed ? '5 4' : undefined}
            />
          );
        }
        return (
          <polyline
            key={index}
            points={primitive.points.map((point) => `${point.x},${point.y}`).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeLinecap="butt"
            strokeDasharray={primitive.dashed ? '5 4' : undefined}
          />
        );
      })}
    </>
  );
}
